<template>
  <div>
    <ContainerWrapper
      full-width-on-mobile
      class="main--white-background"
    >
      <Heading
        class="price-list__heading text-center mb-48"
        kind="h1"
      >
        {{ $t('pages.pricelistNew.heading') }}
        <div
          class="price-list__sub-heading mt-12"
          v-html="$t('pages.pricelistNew.subHeading')"
        />
      </Heading>
      <TariffCardWrapper>
        <template slot="tariffMini">
          <TariffCard
            :is-loading="true"
            v-if="!priceListData"
          />
          <TariffCard
            :is-loading="!priceListData.mini"
            :tariff="priceListData.mini"
            data-test="card_mini_tariff"
            v-else
          />
        </template>
        <template slot="tariffPremium">
          <TariffCard
            :is-loading="true"
            :is-tariff-premium="true"
            v-if="!priceListData"
          />
          <TariffCard
            :tariff="priceListData.premium"
            data-test="card_premium_tariff"
            :is-tariff-premium="true"
            v-else
          />
        </template>
      </TariffCardWrapper>

      <Row
        align="top"
        justify="center"
        type="flex"
        :gutter="12"
      >
        <Column
          v-for="index in 3"
          :key="`feature-${index}`"
          :sm="12"
          :md="8"
        >
          <div class="price-list__feature mb-16">
            <img
              :src="require(`~/images/price_list/feature-${index}.webp`)"
              alt="Feature icon"
              class="price-list__feature-image"
            >
            <div class="price-list__feature-content">
              <Heading
                kind="h3"
                type="h2"
                class="heading--semibold overflow-wrap-break my-0"
              >
                {{ $t(`pages.pricelistNew.features.feature${index}.heading`) }}
              </Heading>
              <p
                class="text--lg mt-12 mb-0"
                v-html="$t(`pages.pricelistNew.features.feature${index}.description`)"
              />
              <div
                class="mt-12"
                v-if="index === 1"
              >
                <a
                  :href="`mailto:${$t('pages.pricelistNew.features.feature1.supportEmail')}`"
                  class="d-block text--lg text--semibold overflow-wrap-break"
                >
                  {{ $t('pages.pricelistNew.features.feature1.supportEmail') }}
                </a>

                <a
                  v-if="$te('pages.pricelistNew.features.feature1.supportPhone')"
                  :href="`tel:${$t('pages.pricelistNew.features.feature1.supportPhone')}`"
                  class="d-block text--lg text--semibold mt-12"
                >
                  {{ $t('pages.pricelistNew.features.feature1.supportPhone') }}
                </a>
              </div>
            </div>
          </div>
        </Column>
      </Row>

      <PriceListDelimiter class="mt-16" />

      <Heading
        kind="h2"
        type="h1"
        class="price-list__section-heading text-center mb-32 mt-0"
      >
        {{ $t('pages.pricelistNew.references.heading') }}
      </Heading>

      <div class="price-list__references">
        <div
          :key="`reference-${index}`"
          class="price-list__reference"
          v-for="index in 4"
        >
          <p
            class="text--xxl text--italic text--secondary-color text--medium mb-16"
            v-html="$t(`pages.pricelistNew.references.reference${index}`)"
          />
          <div class="d-flex align-items-center gap-12">
            <img
              :src="require(`~/images/price_list/reference-${index}.webp`)"
              alt="Photo of our satisfied customer"
              class="flex-shrink-0"
              height="56"
              width="56"
            >
            <span class="text--xxl text--bold">
              {{ $t(`pages.pricelistNew.references.referenceName${index}`) }}
            </span>
          </div>
        </div>
      </div>

      <PriceListDelimiter class="mt-0" />

      <Heading
        kind="h2"
        type="h1"
        class="price-list__section-heading text-center mb-32 mt-64"
      >
        {{ $t('pages.pricelistNew.faq.heading') }}
      </Heading>

      <Collapse
        class="price-list__faq mb-8"
      >
        <CollapseItem
          v-for="index in 4"
          :key="`faq-item-${index}`"
          :title="$t(`pages.pricelistNew.faq.question${index}`)"
          :name="index"
        >
          <div v-html="$t(`pages.pricelistNew.faq.answer${index}`)" />
        </CollapseItem>
      </Collapse>
    </ContainerWrapper>

    <div class="overflow-hidden pb-32 px-xl-16 pt-xl-48 mt-n64 mt-xl-n48">
      <CtaArea
        :button-path="PATH_INVOICE"
        button-size="mega"
        :heading="$t('pages.pricelistNew.cta.heading')"
        :image-url="require(`~/images/people/${genderWithVariant}.webp`)"
        class="cta-area--last-on-page"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PRICELIST_QUERY } from '~/queries';
import { PATH_INVOICE } from '~/router';

export default {
  components: {
    Collapse: () => import('~/ui-library/Collapse.vue'),
    CollapseItem: () => import('~/ui-library/CollapseItem.vue'),
    CtaArea: () => import('~/components/CtaArea.vue'),
    PriceListDelimiter: () => import('~/components/PriceList/PriceListDelimiter.vue'),
    TariffCard: () => import('~/components/v2/Order/TariffCard.vue'),
    TariffCardWrapper: () => import('~/components/v2/Order/TariffCardWrapper.vue'),
  },
  data: () => ({
    PATH_INVOICE,
  }),
  apollo: {
    pricelist: {
      query: PRICELIST_QUERY,
    },
  },
  computed: {
    ...mapGetters('auth', ['isTrial', 'currentPlanName']),
    breadcrumbs() {
      return [
        { key: 'pricelist' },
      ];
    },
    priceListData() {
      if (!this.pricelist) {
        return null;
      }

      return {
        mini: this.pricelist.mini,
        premium: this.pricelist.premium,
      };
    },
    subscription() {
      return {
        planName: this.currentPlanName,
        isTrial: this.isTrial,
      };
    },
    gender() {
      const genders = ['boy', 'girl'];
      return genders[Math.floor(Math.random() * genders.length)];
    },
    genderWithVariant() {
      const number = Math.floor(Math.random() * 11) + 1;
      return `${this.gender}-${number}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.price-list__heading {
  font-size: rem(56);
  line-height: 1.2;

  @media only screen and (max-width: $--md - 1px) {
    font-size: rem(32px);
    line-height: 1.6;
  }
}

.price-list__sub-heading {
  font-size: rem(40);
  font-weight: 600;
  line-height: 1.3;

  @media only screen and (max-width: $--md - 1px) {
    font-size: rem(24px);
    line-height: 1.6;
  }
}

.price-list__section-heading {
  @media only screen and (max-width: $--sm - 1px) {
    font-size: rem(24px);
    font-weight: 600;
  }
}

.price-list__feature {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;

  @media only screen and (max-width: $--sm - 1px) {
    align-items: flex-start;
    flex-direction: row;
    gap: 16px;
    text-align: left;
  }
}

.price-list__feature-content {
  min-width: 0;
}

.price-list__feature-image {
  width: auto;
  height: 120px;
  flex-shrink: 0;

  @media only screen and (max-width: $--xs - 1px) {
    height: 80px;
  }
}

.price-list__references {
  column-count: 2;
  column-gap: 80px;

  .price-list__reference {
    break-inside: avoid;
    margin-bottom: 40px;
  }

  @media only screen and (max-width: $--md - 1px) {
    column-gap: 40px;
  }

  @media only screen and (max-width: $--sm - 1px) {
    column-count: 1;

    .price-list__reference {
      margin-bottom: 32px;
    }
  }
}

.price-list__faq {
  max-width: 800px;
  margin: 0 auto;
}
</style>

<style lang="scss">
.price-list__sub-heading {
  .price-list__highlight {
    font-weight: 600;
    position: relative;
    display: inline-block;

    &::before {
      content: '';
      position: absolute;
      bottom: -26px;
      right: -28px;
      background-image: url('text_highlight.svg');
      background-repeat: no-repeat;
      background-size: contain;
      height: 31px;
      width: 179px;
    }

    @media only screen and (max-width: $--md - 1px) {
      &::before {
        bottom: -18px;
        right: -8px;
        height: 20px;
        width: 114px;
      }
    }
  }
}

.overflow-wrap-break {
  overflow-wrap: break-word;
}
</style>
